import React from 'react';
import isEqual from 'lodash/isEqual';
import { useHistory } from 'react-router-dom';

import { useAuthState } from '@/utils/auth';
import {
  ProjectNotFoundError,
  useAllProjectOptions,
  useBusinessUnitIdToSlug,
  useBusinessUnits,
  useBusinessUnitSlugToId,
  useProjectIdToBusinessUnit,
  useProjectIdToSlug,
  useProjectNameToId,
  useProjectSlugToName,
} from '@/hooks/useProjectOptions';
import { atom, useAtom } from 'jotai';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  MyUserDataFragment,
  RoleNames,
  useGetAllCompaniesForProjectOptionsQuery,
} from '@/generated/codegen';
import { ScopeProvider } from 'jotai-scope';
import { isNotNullish } from '@/utils/nullish';
import {
  ContextOutOfReachError,
  contextLevel,
  isContextReachable,
} from '@/hooks/contextReach';

export class CompanyNotFoundError extends Error {
  constructor(company_name: string) {
    super(company_name);
    this.name = 'CompanyNotFoundError';
    this.message = `${company_name} was not found in the current logged in user's roles. You might not be allowed to access this company.`;
  }
}

export type SelectedContext = {
  company_id: number;
  // Only ever set when a business unit is selected WITHOUT a project — the
  // backend rejects a redundant BU+project pair. When a project is selected,
  // its BU is implied and must not be sent here.
  business_unit_id?: number | null;
  project_id?: number | null;
};

export type SelectedFilter = {
  kind: 'selected';
  value: {
    company_id: number;
    // Always populated when determinable: the explicitly selected BU, or the
    // selected project's BU. Null only when neither can be determined (e.g.
    // "Todos los Proyectos" with no BU chosen).
    business_unit_id: number | null;
    project_id: number | null;
  };
  query: SelectedContext;
  label: string;
  names: { company: string; business_unit: string; project: string };
};

export type EmptyFilter = {
  kind: 'none';
  value: {
    company_id: null;
    business_unit_id: number | null;
    project_id: number | null;
  };
  query: null;
  label: string;
  names: { company: string; business_unit: string; project: string };
};

export type OptionalFilter = EmptyFilter | SelectedFilter;

export type AppContextMode =
  | { kind: 'none' }
  | { kind: 'selected'; filter: SelectedFilter }
  | { kind: 'all-authorized' };

const initialFilter: EmptyFilter = {
  kind: 'none',
  value: {
    company_id: null,
    business_unit_id: null,
    project_id: null,
  },
  query: null,
  label: 'Selecciona un proyecto...',
  names: { company: '', business_unit: '', project: '' },
};
export type CompanyOption = {
  id: number;
  name: string;
  slug: string;
};

const filterAtom = atom<OptionalFilter>(initialFilter);
const filterLoadingAtom = atom(true);
const companyOptionsAtom = atom<CompanyOption[]>([]);
const ForcedFilterContext = React.createContext<OptionalFilter | null>(null);
const AppContextModeContext = React.createContext<AppContextMode>({
  kind: 'none',
});

export const useCompanyOptions = () => useAtomValue(companyOptionsAtom);

export const useOptionalFilterState = () => {
  const forcedFilter = React.useContext(ForcedFilterContext);
  const filter = useAtomValue(filterAtom);

  return forcedFilter ?? filter;
};

export const useSelectedFilterState = (): SelectedFilter => {
  const filter = useOptionalFilterState();

  if (filter.kind !== 'selected') {
    throw new Error(
      'useSelectedFilterState must only be used inside context-required routes'
    );
  }

  return filter;
};

export const useAppContextMode = () => React.useContext(AppContextModeContext);

export const useAllAuthorizedContext = () => {
  const mode = useAppContextMode();

  if (mode.kind !== 'all-authorized') {
    throw new Error(
      'useAllAuthorizedContext must only be used in machinist app'
    );
  }

  return mode;
};

export const useFilterLoading = () => useAtomValue(filterLoadingAtom);

const useCompanyIdToSlug = () => {
  const companyOptions = useCompanyOptions();
  return React.useCallback(
    (id: number) => {
      const company = companyOptions.find((company) => company.id === id);
      if (!company) throw new CompanyNotFoundError(`id: ${id}`);
      return company.slug;
    },
    [companyOptions]
  );
};

export const useCompanyNameToId = () => {
  const companyOptions = useCompanyOptions();
  return React.useCallback(
    (name: string) => {
      const company = companyOptions.find((company) => company.name === name);
      if (!company) throw new CompanyNotFoundError(name);
      return company.id;
    },
    [companyOptions]
  );
};

export const useCompanySlugToName = () => {
  const companyOptions = useCompanyOptions();
  return React.useCallback(
    (slug: string) => {
      const company = companyOptions.find((company) => company.slug === slug);
      if (!company) throw new CompanyNotFoundError(slug);
      return company.name;
    },
    [companyOptions]
  );
};

export const useMakeFilteredUrl = () => {
  const filter = useOptionalFilterState();
  const mode = useAppContextMode();
  const projectIdToSlug = useProjectIdToSlug();
  const companyIdToSlug = useCompanyIdToSlug();
  const businessUnitIdToSlug = useBusinessUnitIdToSlug();
  const projectIdToBusinessUnit = useProjectIdToBusinessUnit();
  return React.useCallback(
    (
      url: string,
      optionsParam?: {
        company_id: number;
        business_unit_id?: number | null;
        project_id?: number;
      } | null,
      scope = 'project'
    ) => {
      if (mode.kind === 'all-authorized') return url;
      const options =
        optionsParam || (mode.kind === 'selected' ? mode.filter : filter);
      const company_id =
        'value' in options ? options.value.company_id : options.company_id;
      const project_id =
        'value' in options ? options.value.project_id : options.project_id;
      let business_unit_id =
        ('value' in options
          ? options.value.business_unit_id
          : options.business_unit_id) ?? null;

      // canonical URL always carries the project's own BU, even if the
      // caller only passed a project_id
      if (project_id && !business_unit_id) {
        try {
          business_unit_id = projectIdToBusinessUnit(project_id).id;
        } catch {
          business_unit_id = null;
        }
      }

      let expectedPath = company_id ? '/' + companyIdToSlug(company_id) : '';

      if (scope === 'project') {
        if (business_unit_id)
          expectedPath += '/' + businessUnitIdToSlug(business_unit_id);
        if (project_id) expectedPath += '/' + projectIdToSlug(project_id);
      }

      return expectedPath + url;
    },
    [
      businessUnitIdToSlug,
      companyIdToSlug,
      filter,
      mode,
      projectIdToBusinessUnit,
      projectIdToSlug,
    ]
  );
};

export const useMakeFilteredUrlFromPath = () => {
  const filter = useOptionalFilterState();
  const mode = useAppContextMode();
  const projectIdToSlug = useProjectIdToSlug();
  const companyIdToSlug = useCompanyIdToSlug();
  const businessUnitIdToSlug = useBusinessUnitIdToSlug();
  const projectIdToBusinessUnit = useProjectIdToBusinessUnit();
  return React.useCallback(
    (
      path: string,
      optionsParam?: {
        company_id: number;
        business_unit_id?: number | null;
        project_id?: number;
      } | null,
      scope = 'project'
    ) => {
      if (mode.kind === 'all-authorized')
        return path
          .replace('/:company?', '')
          .replace('/:businessUnit?', '')
          .replace('/:project?', '');
      const options =
        optionsParam || (mode.kind === 'selected' ? mode.filter : filter);
      const company_id =
        'value' in options ? options.value.company_id : options.company_id;
      const project_id =
        'value' in options ? options.value.project_id : options.project_id;
      let business_unit_id =
        ('value' in options
          ? options.value.business_unit_id
          : options.business_unit_id) ?? null;

      if (project_id && !business_unit_id) {
        try {
          business_unit_id = projectIdToBusinessUnit(project_id).id;
        } catch {
          business_unit_id = null;
        }
      }

      const concreteUrl = path
        .replace(
          '/:company?',
          company_id ? `/${companyIdToSlug(company_id)}` : ''
        )
        .replace(
          '/:businessUnit?',
          scope === 'project' && business_unit_id
            ? `/${businessUnitIdToSlug(business_unit_id)}`
            : ''
        )
        .replace(
          '/:project?',
          scope === 'project' && project_id
            ? `/${projectIdToSlug(project_id)}`
            : ''
        );

      return concreteUrl;
    },
    [
      businessUnitIdToSlug,
      companyIdToSlug,
      filter,
      mode,
      projectIdToBusinessUnit,
      projectIdToSlug,
    ]
  );
};

export const useSetUrlById = () => {
  const make = useMakeFilteredUrl();
  let history = useHistory<{ from: any }>();

  return React.useCallback(
    ({
      company_id,
      business_unit_id,
      project_id,
    }: {
      company_id?: number | null;
      business_unit_id?: number | null;
      project_id?: number | null;
    }) => {
      if (!company_id) {
        history.push('/');
        return;
      }

      const currentUrl =
        history.location.pathname +
        history.location.search +
        history.location.hash;

      const expectedPath = make('');
      let newPath = make('', {
        company_id,
        business_unit_id: business_unit_id ?? undefined,
        project_id: project_id ?? undefined,
      });

      const newUrl = currentUrl
        .replace('choose_company', '')
        .replace('create', '')
        .replace(expectedPath, newPath)
        .replace(/\/\//g, '/');

      history.push(newUrl);
    },
    [history, make]
  );
};

export const useSetFilterFromSlug = () => {
  const companyNameToId = useCompanyNameToId();
  const projectSlugToName = useProjectSlugToName();
  const projectNameToId = useProjectNameToId();
  const businessUnitSlugToId = useBusinessUnitSlugToId();
  const businessUnits = useBusinessUnits();
  const companyOptions = useAllProjectOptions();
  const projectIdToBusinessUnit = useProjectIdToBusinessUnit();
  const [filter, setFilter] = useAtom(filterAtom);
  const setFilterLoading = useSetAtom(filterLoadingAtom);
  const companySlugToName = useCompanySlugToName();
  const { user: loggedInUser } = useAuthState();

  return React.useCallback(
    (slugs?: {
      companySlug: string;
      businessUnitSlug?: string;
      projectSlug?: string;
    }) => {
      if (!slugs) {
        setFilter(initialFilter);
        return;
      }
      const { companySlug, businessUnitSlug, projectSlug } = slugs;
      if (!loggedInUser) return;
      const companyName = companySlugToName(companySlug);
      const company_id = companyNameToId(companyName);

      const projectName = projectSlug
        ? projectSlugToName(projectSlug, company_id)
        : undefined;
      const project_id = projectName
        ? projectNameToId(projectName, company_id)
        : null;

      // Resolve the BU: an explicit slug is authoritative when there is no
      // project; when a project is selected its own BU is authoritative and
      // must match any explicit BU slug (legacy URLs only carry the project).
      let business_unit_id: number | null = null;
      let businessUnitName = '';
      if (businessUnitSlug) {
        business_unit_id = businessUnitSlugToId(businessUnitSlug, company_id);
        businessUnitName =
          businessUnits.find((bu) => bu.id === business_unit_id)?.name ?? '';
      }
      if (project_id !== null) {
        const projectBusinessUnit = projectIdToBusinessUnit(project_id);
        if (businessUnitSlug && projectBusinessUnit.id !== business_unit_id) {
          // the project doesn't belong to the given business unit
          throw new ProjectNotFoundError(projectSlug!);
        }
        business_unit_id = projectBusinessUnit.id;
        businessUnitName = projectBusinessUnit.name;
      }

      // The slugs resolving only proves the context exists: the company
      // options hold every company the user has any role in, the business
      // units include grouping-only ones and the projects list the whole
      // company. Landing on it also needs a role that reaches it (BIL-5009).
      const context = {
        company_id,
        business_unit_id: project_id ? null : business_unit_id,
        project_id,
      };
      if (!isContextReachable(companyOptions, businessUnits, context)) {
        throw new ContextOutOfReachError(
          contextLevel(context),
          projectName || businessUnitName || companyName
        );
      }

      const newFilter: SelectedFilter = {
        kind: 'selected',
        value: {
          company_id,
          business_unit_id,
          project_id,
        },
        label: companyName
          ? `${projectName || businessUnitName || 'Todos los Proyectos'}`
          : 'Selecciona un proyecto',
        names: {
          company: companyName,
          business_unit: businessUnitName,
          project: projectName ?? '',
        },
        query: {
          company_id,
          ...(project_id
            ? { project_id }
            : business_unit_id
              ? { business_unit_id }
              : {}),
        },
      };

      if (!isEqual(newFilter, filter)) {
        setFilter(newFilter);
        setFilterLoading(false);
      }
    },
    [
      businessUnits,
      businessUnitSlugToId,
      companyNameToId,
      companyOptions,
      companySlugToName,
      filter,
      loggedInUser,
      projectIdToBusinessUnit,
      projectNameToId,
      projectSlugToName,
      setFilter,
      setFilterLoading,
    ]
  );
};

export const ManageFilterOptions = () => {
  const { user: loggedInUser } = useAuthState();
  const isSuperRoot = !!loggedInUser?.roles.some(
    (role) => role?.role === RoleNames.SuperRoot
  );
  // This is for a superRoot user... in that case the company info does not come in the roles
  // array, so we have to get it ourselves
  const { data: allCompaniesData, loading: loadingCompanyData } =
    useGetAllCompaniesForProjectOptionsQuery({
      skip: !isSuperRoot,
      notifyOnNetworkStatusChange: true,
    });
  const setFilter = useSetAtom(filterAtom);
  const setFilterLoading = useSetAtom(filterLoadingAtom);
  const setCompanyOptions = useSetAtom(companyOptionsAtom);
  const roles = React.useMemo(
    () => (loggedInUser?.roles?.[0] ? loggedInUser.roles : []),
    [loggedInUser]
  );

  React.useEffect(() => {
    if (!loggedInUser) {
      setCompanyOptions([]);
      return;
    }
    if (!roles) {
      return;
    } else {
      let companyOptions = [
        ...roles,
        ...(allCompaniesData?.getAllCompanies.items?.map((company) => ({
          company,
        })) ?? []),
      ]
        .filter(
          (
            role
          ): role is NonNullable<MyUserDataFragment['roles'][number]> & {
            company: NonNullable<
              NonNullable<
                NonNullable<MyUserDataFragment['roles'][number]>['company']
              >
            >;
          } => !!role?.company
        )
        .reduce(
          (acc, role) =>
            role
              ? {
                  ...acc,
                  [role.company!.name]: {
                    id: role.company.id,
                    slug: role.company.slug,
                    name: role.company.name,
                  },
                }
              : acc,
          {}
        );

      setCompanyOptions(Object.values(companyOptions));
      setFilterLoading(isSuperRoot && loadingCompanyData);
    }
  }, [
    allCompaniesData?.getAllCompanies.items,
    isSuperRoot,
    loadingCompanyData,
    loggedInUser,
    roles,
    setCompanyOptions,
    setFilter,
    setFilterLoading,
  ]);

  return null;
};

export const CompanyFilterContext: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <ScopeProvider atoms={[filterAtom]}>{children}</ScopeProvider>;
};

export const SelectedContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const filter = useSelectedFilterState();
  return (
    <AppContextModeContext.Provider value={{ kind: 'selected', filter }}>
      {children}
    </AppContextModeContext.Provider>
  );
};

export const AllAuthorizedContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <AppContextModeContext.Provider value={{ kind: 'all-authorized' }}>
      {children}
    </AppContextModeContext.Provider>
  );
};

const useForcedSelectedFilter = ({
  company_id,
  project_id,
}: {
  company_id: number;
  project_id: number | null;
}) => {
  const companyOptions = useCompanyOptions();
  const companyName = companyOptions.find(
    (company) => company.id === company_id
  )?.name;
  const projectIdToSlug = useProjectIdToSlug();
  const projectSlugToName = useProjectSlugToName();
  const projectIdToBusinessUnit = useProjectIdToBusinessUnit();
  const projectName = React.useMemo(() => {
    if (!project_id) return '';

    try {
      return projectSlugToName(projectIdToSlug(project_id), company_id) ?? '';
    } catch {
      return '';
    }
  }, [company_id, projectIdToSlug, projectSlugToName, project_id]);

  const businessUnit = React.useMemo(() => {
    if (!project_id) return null;

    try {
      return projectIdToBusinessUnit(project_id) ?? null;
    } catch {
      return null;
    }
  }, [project_id, projectIdToBusinessUnit]);

  return React.useMemo(
    (): SelectedFilter => ({
      kind: 'selected',
      value: {
        company_id,
        business_unit_id: businessUnit?.id ?? null,
        project_id,
      },
      label: projectName || 'Todos los Proyectos',
      names: {
        company: companyName ?? '',
        business_unit: businessUnit?.name ?? '',
        project: projectName,
      },
      query: {
        company_id,
        ...(project_id ? { project_id } : {}),
      },
    }),
    [companyName, company_id, projectName, project_id, businessUnit]
  );
};

export const ForcedCompanyFilterContext: React.FC<{
  company_id?: number;
  project_id?: number | null;
  children: React.ReactNode;
}> = ({ children, company_id, project_id }) => {
  const filter = useForcedSelectedFilter({
    company_id: company_id ?? 0,
    project_id: project_id ?? null,
  });

  if (!isNotNullish(company_id)) {
    return <>{children}</>;
  }

  return (
    <ForcedFilterContext.Provider value={filter}>
      <AppContextModeContext.Provider value={{ kind: 'selected', filter }}>
        {children}
      </AppContextModeContext.Provider>
    </ForcedFilterContext.Provider>
  );
};
